import React from "react";
import LegalDocument from "./LegalDocument";

/* =========================================================================
 *  Apsession Privacy Policy — the full policy, verbatim from the signed PDF
 *  (Effective 1 September 2026).
 *
 *  Reached from the footer link present on every page. Only the text lives
 *  here; the masthead, table of contents, and typography come from
 *  <LegalDocument>, shared with the Terms of Service and Organizer Agreement.
 *
 *  A legal revision is a content edit in this file alone — and the sidebar
 *  table of contents renders off the same SECTIONS list as the body, so the
 *  two cannot drift apart.
 * ========================================================================= */

export const POLICY_EFFECTIVE_DATE = "September 1, 2026";
export const POLICY_LAST_UPDATED = "September 1, 2026";

const INTRO = [
    "Apsession Limited (“Apsession,” “we,” “us,” or “our”) respects your privacy and is committed to protecting the personal information entrusted to us.",
    "This Privacy Policy explains how Apsession collects, uses, stores, shares, and protects personal information when you use the Apsession website, mobile applications, event management platform, ticketing services, ticket scanning services, Digital Ticket Outlets, Verified Ticket Ambassador services, or other products and services operated by Apsession (collectively, the “Services”).",
    "By accessing or using the Services, you acknowledge the practices described in this Privacy Policy.",
];

const SECTIONS = [
    {
        id: "who-we-are",
        title: "1. Who We Are",
        blocks: [
            { p: "Apsession Limited operates an event discovery, management, ticketing, sales, and ticket distribution platform." },
            { p: "Apsession enables users to discover events and purchase tickets and enables Event Organizers and authorized sellers to create, manage, promote, sell, distribute, and validate tickets." },
            { p: "Our Services may include:" },
            {
                list: [
                    "Online ticket purchases",
                    "Event creation and management",
                    "Digital ticket delivery",
                    "QR-code ticket validation",
                    "Online and offline ticket scanning",
                    "Digital Ticket Outlets",
                    "Verified Ticket Ambassadors",
                    "Event managers and ticket sellers",
                    "Gate Staff and gate sales",
                    "Event marketing services",
                    "Payment processing",
                    "Organizer payouts",
                    "Sales reporting and analytics",
                    "Customer support",
                ],
            },
        ],
    },
    {
        id: "information-we-collect",
        title: "2. Information We Collect",
        blocks: [
            { p: "The information we collect depends on how you interact with Apsession." },
            {
                sub: "2.1 Account Information",
                blocks: [
                    { p: "When you create or maintain an Apsession account, we may collect:" },
                    {
                        list: [
                            "Full name",
                            "Email address",
                            "Telephone number",
                            "Username",
                            "Password or authentication credentials",
                            "Country",
                            "Parish, state, province, or region",
                            "City",
                            "Account type and permissions",
                            "Account activity",
                        ],
                    },
                ],
            },
            {
                sub: "2.2 Ticket Purchase Information",
                blocks: [
                    { p: "When you purchase, receive, or are issued tickets, we may collect:" },
                    {
                        list: [
                            "Purchaser name",
                            "Attendee name, where applicable",
                            "Email address",
                            "Telephone number",
                            "Event purchased",
                            "Ticket type",
                            "Ticket quantity",
                            "Ticket number or identifier",
                            "Order number",
                            "Purchase amount",
                            "Applicable fees",
                            "Payment status",
                            "Transaction date and time",
                            "Ticket delivery method",
                            "Ticket usage or scanning status",
                        ],
                    },
                    { p: "Tickets may be delivered through channels including email, SMS, WhatsApp, or other supported electronic methods." },
                ],
            },
            {
                sub: "2.3 Payment Information",
                blocks: [
                    { p: "Payments made through Apsession may be processed by third-party payment processors." },
                    { p: "Depending on the payment method and processor, information may include:" },
                    {
                        list: [
                            "Payment method",
                            "Transaction reference",
                            "Authorization status",
                            "Payment response codes",
                            "Card type",
                            "Last four digits of a payment card",
                            "Billing information",
                            "Fraud-prevention or transaction-risk information",
                        ],
                    },
                    { p: "Apsession generally does not need to store complete payment-card numbers when payment information is securely handled by an authorized payment processor." },
                ],
            },
            {
                sub: "2.4 Event Organizer Information",
                blocks: [
                    { p: "If you create, manage, or sell tickets for an event, we may collect:" },
                    {
                        list: [
                            "Name",
                            "Business or organization name",
                            "Contact information",
                            "Event information",
                            "Event media",
                            "Ticket pricing",
                            "Sales information",
                            "Bank or payout information",
                            "Transaction and settlement information",
                            "Tax, identification, or compliance information where required",
                            "Event performance and analytics",
                        ],
                    },
                ],
            },
            {
                sub: "2.5 Verified Ticket Ambassador Information",
                blocks: [
                    { p: "Applicants and participants in the Verified Ticket Ambassador program may be required to provide information such as:" },
                    {
                        list: [
                            "Full legal name",
                            "Country",
                            "Parish, state, or region",
                            "City",
                            "Address",
                            "Contact information",
                            "Biography",
                            "Ticket-selling experience",
                            "Events previously promoted or sold",
                            "Social-media profile links",
                            "Government-issued identification",
                            "Proof of address",
                            "Verification documents",
                            "Ticket sales and commission information",
                            "Remittance information",
                        ],
                    },
                    { p: "This information may be used to verify identity, assess eligibility, prevent fraud, administer the program, and meet legal or compliance obligations." },
                ],
            },
            {
                sub: "2.6 Digital Ticket Outlet Information",
                blocks: [
                    { p: "For businesses or individuals operating Digital Ticket Outlets, we may collect:" },
                    {
                        list: [
                            "Business or operator name",
                            "Contact information",
                            "Location",
                            "Account information",
                            "Ticket sales",
                            "Funds collected",
                            "Amounts due to Apsession",
                            "Remittance history",
                            "Banking or payout information",
                            "Transaction records",
                        ],
                    },
                ],
            },
            {
                sub: "2.7 Event Managers, Sellers and Gate Staff",
                blocks: [
                    { p: "Where Event Organizers authorize other persons to assist with an event, we may collect and process information relating to:" },
                    {
                        list: [
                            "Event Managers",
                            "Ticket sellers",
                            "Gate Staff",
                            "Ticket scanners",
                            "Other authorized event personnel",
                        ],
                    },
                    { p: "This may include:" },
                    {
                        list: [
                            "Name",
                            "Email address",
                            "Telephone number",
                            "Account information",
                            "Assigned event",
                            "Assigned role",
                            "Permissions",
                            "Sales activity",
                            "Scanning activity",
                            "Account activity",
                        ],
                    },
                ],
            },
            {
                sub: "2.8 Device and Technical Information",
                blocks: [
                    { p: "When you use our Services, we may automatically collect information such as:" },
                    {
                        list: [
                            "IP address",
                            "Device type",
                            "Operating system",
                            "Browser type",
                            "App version",
                            "Device identifiers",
                            "Login information",
                            "Date and time of access",
                            "Pages or features accessed",
                            "Crash and diagnostic information",
                            "Approximate location derived from technical information",
                        ],
                    },
                ],
            },
            {
                sub: "2.9 Communications",
                blocks: [
                    { p: "If you communicate with Apsession, we may retain those communications, including communications through:" },
                    {
                        list: [
                            "Email",
                            "Customer support",
                            "WhatsApp",
                            "SMS",
                            "Social media",
                            "In-app support",
                            "Other communication channels",
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: "how-we-use",
        title: "3. How We Use Personal Information",
        blocks: [
            { p: "We may use personal information to:" },
            {
                list: [
                    "Create and manage accounts",
                    "Process ticket purchases",
                    "Generate and deliver digital tickets",
                    "Send tickets through email, SMS, WhatsApp, or other supported channels",
                    "Process payments",
                    "Facilitate Organizer payouts",
                    "Track and reconcile transactions",
                    "Validate tickets",
                    "Support online and offline ticket scanning",
                    "Operate Digital Ticket Outlets",
                    "Operate the Verified Ticket Ambassador program",
                    "Support Event Managers, sellers, and Gate Staff",
                    "Calculate commissions",
                    "Track remittances",
                    "Verify identities",
                    "Prevent fraud and unauthorized transactions",
                    "Review events before publication",
                    "Provide customer support",
                    "Send transaction confirmations and service notifications",
                    "Send important event updates",
                    "Improve our Services",
                    "Analyze platform performance",
                    "Develop new products and features",
                    "Diagnose technical problems",
                    "Protect users, Organizers, sellers, and Apsession",
                    "Enforce our Terms of Service and Organizer Agreement",
                    "Comply with legal and regulatory obligations",
                ],
            },
        ],
    },
    {
        id: "communications-marketing",
        title: "4. Communications and Marketing",
        blocks: [
            { p: "We may send transactional communications relating to your account or activity, including:" },
            {
                list: [
                    "Ticket confirmations",
                    "Ticket delivery messages",
                    "Payment confirmations",
                    "Event updates",
                    "Account-security alerts",
                    "Password resets",
                    "Payout notifications",
                    "Remittance notices",
                    "Ambassador or manager invitations",
                    "Ticket sales notifications",
                    "Other operational messages",
                ],
            },
            { p: "These communications may be necessary to provide the Services and may not always be optional." },
            { p: "Where permitted by law, we may also send promotional communications regarding events, Apsession products, upgrades, offers, or services." },
            { p: "Users may unsubscribe from eligible marketing communications using the unsubscribe method provided in the communication." },
            { p: "Opting out of marketing does not prevent Apsession from sending necessary transactional, security, or service-related communications." },
        ],
    },
    {
        id: "how-we-share",
        title: "5. How We Share Information",
        blocks: [
            { p: "Apsession does not sell personal information as a business model." },
            { p: "We may disclose personal information where reasonably necessary to provide the Services." },
            {
                sub: "5.1 Event Organizers",
                blocks: [
                    { p: "When you purchase or receive a ticket, relevant information may be made available to the Organizer responsible for the event." },
                    { p: "This may include information needed for:" },
                    {
                        list: [
                            "Ticket administration",
                            "Attendee management",
                            "Customer service",
                            "Event communications",
                            "Entry validation",
                            "Refund administration",
                            "Event operations",
                        ],
                    },
                ],
            },
            {
                sub: "5.2 Payment Processors and Financial Institutions",
                blocks: [
                    { p: "Information may be shared with payment processors, acquiring banks, card networks, financial institutions, fraud-prevention providers, and payout providers to process and secure transactions." },
                ],
            },
            {
                sub: "5.3 Service Providers",
                blocks: [
                    { p: "We may use third-party companies to provide services including:" },
                    {
                        list: [
                            "Cloud hosting",
                            "Data storage",
                            "Email delivery",
                            "SMS delivery",
                            "Messaging",
                            "Analytics",
                            "Customer support",
                            "Identity verification",
                            "Fraud prevention",
                            "Payment processing",
                        ],
                    },
                    { p: "These providers may process personal information on our behalf to perform their services." },
                ],
            },
            {
                sub: "5.4 Sellers and Event Personnel",
                blocks: [
                    { p: "Where necessary to operate an event, limited information may be accessible to authorized:" },
                    {
                        list: [
                            "Event Managers",
                            "Ticket scanners",
                            "Gate Staff",
                            "Digital Ticket Outlets",
                            "Verified Ticket Ambassadors",
                            "Other authorized event personnel",
                        ],
                    },
                    { p: "Access may be restricted according to each user's role and permissions." },
                ],
            },
            {
                sub: "5.5 Legal and Safety Disclosures",
                blocks: [
                    { p: "We may disclose information where reasonably necessary to:" },
                    {
                        list: [
                            "Comply with applicable law",
                            "Respond to lawful government or regulatory requests",
                            "Respond to court orders",
                            "Investigate fraud",
                            "Prevent illegal activity",
                            "Protect Apsession, our users, or the public",
                            "Enforce agreements or policies",
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: "payment-security",
        title: "6. Payment Security",
        blocks: [
            { p: "Payment transactions may be processed through third-party payment providers." },
            { p: "Apsession uses reasonable administrative, organizational, and technical measures intended to protect transaction information." },
            { p: "No electronic system can be guaranteed to be completely secure. Users should protect their account credentials and immediately notify Apsession if they believe their account or ticket information has been compromised." },
        ],
    },
    {
        id: "data-retention",
        title: "7. Data Retention",
        blocks: [
            { p: "We retain personal information only for as long as reasonably necessary for the purpose for which it was collected and as required or permitted by applicable law." },
            { p: "Different categories of information may have different retention periods." },
            { p: "For example, transaction, accounting, payout, fraud-prevention, dispute, chargeback, remittance, and compliance records may need to be retained after an account has been closed or an event has ended." },
            { p: "When information is no longer reasonably required, we may delete, anonymize, or securely dispose of it in accordance with applicable requirements." },
        ],
    },
    {
        id: "international-processing",
        title: "8. International Data Processing",
        blocks: [
            { p: "Some of Apsession's technology providers, payment processors, messaging providers, cloud infrastructure providers, or other service providers may process or store information outside Jamaica." },
            { p: "Where personal information is transferred internationally, Apsession will take appropriate measures required by applicable data-protection law to protect that information." },
        ],
    },
    {
        id: "your-rights",
        title: "9. Your Privacy Rights",
        blocks: [
            { p: "Subject to applicable law and any lawful exceptions, you may have rights concerning your personal information, including the right to:" },
            {
                list: [
                    "Be informed about how your information is processed",
                    "Request access to your personal information",
                    "Request correction of inaccurate information",
                    "Request that certain processing be restricted",
                    "Withdraw consent where processing depends upon consent",
                    "Object to certain uses of your information",
                    "Request information concerning certain automated decisions",
                    "Make a complaint regarding the handling of your personal information",
                ],
            },
            { p: "We may need to verify your identity before completing a privacy-related request." },
            { p: "Certain information may need to be retained despite a deletion or restriction request where retention is necessary for legal, financial, fraud-prevention, contractual, dispute-resolution, accounting, or security purposes." },
        ],
    },
    {
        id: "cookies",
        title: "10. Cookies and Similar Technologies",
        blocks: [
            { p: "Apsession may use cookies and similar technologies to:" },
            {
                list: [
                    "Keep users signed in",
                    "Remember preferences",
                    "Maintain security",
                    "Understand usage",
                    "Measure platform performance",
                    "Improve functionality",
                    "Support analytics and marketing",
                ],
            },
            { p: "Users may be able to control certain cookies through their browser or device settings." },
            { p: "Disabling certain technologies may affect the operation of some Services." },
        ],
    },
    {
        id: "location",
        title: "11. Location Information",
        blocks: [
            { p: "Certain Apsession features may use location information where necessary or where permission has been granted." },
            { p: "Location information may be used to:" },
            {
                list: [
                    "Provide relevant event information",
                    "Support event operations",
                    "Prevent fraud",
                    "Improve security",
                    "Provide location-dependent features",
                ],
            },
            { p: "Where device-level permission is required, you may control that permission through your device settings." },
        ],
    },
    {
        id: "ticket-scanning",
        title: "12. Ticket Scanning",
        blocks: [
            { p: "When a ticket is scanned, Apsession may process information including:" },
            {
                list: [
                    "Ticket identifier",
                    "Event",
                    "Scan status",
                    "Scan date and time",
                    "Scanning user or device",
                    "Validation result",
                ],
            },
            { p: "This information may be used to prevent duplicate entry, maintain event records, support customer service, investigate disputes, and provide Organizer analytics." },
        ],
    },
    {
        id: "offline-scanning",
        title: "13. Offline Ticket Scanning",
        blocks: [
            { p: "Apsession may permit authorized devices to temporarily store information necessary to validate tickets without an active internet connection." },
            { p: "When connectivity becomes available, ticket-validation information may synchronize with Apsession's systems." },
            { p: "We take reasonable measures designed to limit the information stored on scanning devices and protect ticket data against unauthorized access." },
        ],
    },
    {
        id: "fraud-prevention",
        title: "14. Fraud Prevention and Risk Management",
        blocks: [
            { p: "Apsession may process account, transaction, device, payment, event, and behavioral information to:" },
            {
                list: [
                    "Detect suspicious transactions",
                    "Prevent account abuse",
                    "Prevent unauthorized payments",
                    "Prevent fraudulent events",
                    "Prevent ticket duplication",
                    "Assess transaction risk",
                    "Investigate chargebacks",
                    "Protect users and Apsession",
                ],
            },
            { p: "Some fraud-prevention activities may involve automated systems." },
            { p: "Where required by applicable law, users may have rights concerning certain decisions based solely on automated processing." },
        ],
    },
    {
        id: "identity-verification",
        title: "15. Identity Verification",
        blocks: [
            { p: "Apsession may require identity or business verification for selected Services, including:" },
            {
                list: [
                    "Event Organizers",
                    "Verified Ticket Ambassadors",
                    "Digital Ticket Outlets",
                    "Payout recipients",
                    "Other higher-risk or financial functions",
                ],
            },
            { p: "Verification documents may include government-issued identification, proof of address, banking information, or business documentation." },
            { p: "Such information may be processed by Apsession or authorized verification providers." },
        ],
    },
    {
        id: "childrens-privacy",
        title: "16. Children's Privacy",
        blocks: [
            { p: "Apsession is not intended to knowingly collect personal information directly from children who are not legally capable of providing the required consent, except where permitted by law and appropriate authorization has been provided." },
            { p: "Events listed on Apsession may have their own age restrictions." },
            { p: "Event Organizers are responsible for accurately communicating applicable event-entry requirements." },
        ],
    },
    {
        id: "organizers-as-users",
        title: "17. Event Organizers as Users of Personal Information",
        blocks: [
            { p: "Event Organizers who receive purchaser or attendee information through Apsession must use that information responsibly and only for legitimate purposes associated with their events or as otherwise permitted by law." },
            { p: "Organizers must not:" },
            {
                list: [
                    "Unlawfully sell personal information",
                    "Improperly disclose personal information",
                    "Use purchaser information for unlawful purposes",
                    "Provide personal information to unauthorized third parties",
                    "Use Apsession customer data in violation of applicable privacy law",
                ],
            },
            { p: "Depending on the circumstances, an Organizer may have independent obligations under applicable privacy and data-protection laws." },
        ],
    },
    {
        id: "security",
        title: "18. Security",
        blocks: [
            { p: "Apsession maintains reasonable technical and organizational safeguards designed to protect personal information against:" },
            {
                list: [
                    "Unauthorized access",
                    "Unauthorized disclosure",
                    "Alteration",
                    "Loss",
                    "Destruction",
                    "Misuse",
                ],
            },
            { p: "Security measures may include:" },
            {
                list: [
                    "Access controls",
                    "Encryption",
                    "Authentication controls",
                    "Monitoring",
                    "Logging",
                    "Backups",
                    "Restricted internal access",
                    "Other appropriate safeguards",
                ],
            },
            { p: "No security system is completely immune from risk." },
        ],
    },
    {
        id: "data-breaches",
        title: "19. Data Breaches",
        blocks: [
            { p: "If Apsession becomes aware of a personal-data breach, we will investigate and respond in accordance with applicable law." },
            { p: "Where legally required, Apsession will notify the appropriate regulatory authority and affected individuals." },
        ],
    },
    {
        id: "third-party-services",
        title: "20. Third-Party Services",
        blocks: [
            { p: "Our Services may contain links to or integrate with websites, applications, payment services, messaging services, social-media services, and other services operated by third parties." },
            { p: "Apsession does not control the independent privacy practices of those third parties." },
            { p: "Their collection and use of information may be governed by their own privacy policies." },
        ],
    },
    {
        id: "account-closure",
        title: "21. Account Closure and Deletion Requests",
        blocks: [
            { p: "Users may request closure of an Apsession account through available account controls or by contacting Apsession." },
            { p: "Closing an account does not necessarily result in immediate deletion of all associated information." },
            { p: "Apsession may retain information where reasonably necessary for:" },
            {
                list: [
                    "Transaction records",
                    "Accounting",
                    "Fraud prevention",
                    "Chargebacks",
                    "Refunds",
                    "Legal obligations",
                    "Regulatory compliance",
                    "Security",
                    "Dispute resolution",
                ],
            },
            { p: "Information no longer required may be deleted or anonymized in accordance with applicable law and Apsession retention practices." },
        ],
    },
    {
        id: "changes",
        title: "22. Changes to This Privacy Policy",
        blocks: [
            { p: "We may update this Privacy Policy periodically to reflect changes to:" },
            {
                list: [
                    "Our Services",
                    "Technology",
                    "Business practices",
                    "Legal requirements",
                    "Security practices",
                ],
            },
            { p: "The updated policy will display a revised “Last Updated” date." },
            { p: "Where required or appropriate, we may provide additional notice of material changes." },
        ],
    },
    {
        id: "contact",
        title: "23. Contact Apsession",
        blocks: [
            { p: "Questions, concerns, and privacy requests may be submitted to:" },
            { contact: true },
            { p: "Requests relating specifically to personal-data rights should clearly state that the request is a Privacy/Data Protection Request so it can be appropriately reviewed." },
        ],
    },
    {
        id: "governing-law",
        title: "24. Governing Privacy Law",
        blocks: [
            { p: "Apsession processes personal information in accordance with applicable privacy and data-protection requirements, including the Data Protection Act, 2020 of Jamaica, where applicable." },
            { p: "Nothing in this Privacy Policy is intended to limit any rights available to an individual under applicable law." },
        ],
    },
];

export default function PrivacyPolicy() {
    return (
        <LegalDocument
            title="Privacy Policy"
            path="/privacy-policy"
            effectiveDate={POLICY_EFFECTIVE_DATE}
            lastUpdated={POLICY_LAST_UPDATED}
            intro={INTRO}
            sections={SECTIONS}
        />
    );
}
